import React, { useState, useMemo, useCallback } from 'react';
import { diff_match_patch } from 'diff-match-patch';
import { Box, Paper, Typography, TextField, Stack, useTheme, Button } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function JsonDiff() {
  const theme = useTheme();
  const [json1, setJson1] = useState('{\n  "name": "UmbrellaCo",\n  "status": "active"\n}');
  const [json2, setJson2] = useState('{\n  "name": "UmbrellaCo",\n  "status": "inactive",\n  "newField": true\n}');
  const [error, setError] = useState(null);

  const formatInputs = useCallback(() => {
    try {
      const parsed1 = JSON.parse(json1);
      const parsed2 = JSON.parse(json2);
      setJson1(JSON.stringify(parsed1, null, 2));
      setJson2(JSON.stringify(parsed2, null, 2));
      setError(null);
    } catch (e) {
      setError('Invalid JSON. Cannot format before diffing.');
    }
  }, [json1, json2]);

  const diffHtml = useMemo(() => {
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(json1, json2);
    dmp.diff_cleanupSemantic(diff);
    return dmp.diff_prettyHtml(diff);
  }, [json1, json2]);

  const stats = useMemo(() => {
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(json1, json2);
    const additions = diff.filter(part => part[0] === 1).length;
    const deletions = diff.filter(part => part[0] === -1).length;
    return { additions, deletions };
  }, [json1, json2]);

  return (
    <>
      <SEO
        title="JSON Diff Tool | Compare JSON Files | UmbrellaCo"
        description="Compare two JSON objects to find differences instantly. Highlight additions, deletions, and modifications with our free online JSON diff tool."
        keywords="json diff, compare json, json comparison, json difference checker, online json diff"
        url="/json-tools/diff"
        jsonLd={createSoftwareAppSchema(
          'JSON Diff Tool',
          'Compare two JSON objects to find differences instantly.',
          'https://umbrellaco.in/json-tools/diff',
          ['JSON Comparison', 'Diff Tool', 'Developer Utilities']
        )}
      />
      <ToolPageContainer
        title="JSON Diff"
        description="Compare two JSON objects side-by-side to highlight additions, deletions, and modifications."
      >

      <Box sx={{ mb: 2 }}>
        <Button onClick={formatInputs} startIcon={<FormatAlignLeftIcon />} variant="outlined">
          Format Both Before Comparing
        </Button>
        {error && <Typography color="error" sx={{ ml: 2, display: 'inline' }}>{error}</Typography>}
      </Box>

      <Box sx={{ flexGrow: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, minHeight: 0 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Original JSON</Typography>
          <TextField
            multiline
            value={json1}
            onChange={(e) => setJson1(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%', fontFamily: 'monospace' }, '& textarea': { height: '100% !important' } }}
          />
        </Paper>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Changed JSON</Typography>
          <TextField
            multiline
            value={json2}
            onChange={(e) => setJson2(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%', fontFamily: 'monospace' }, '& textarea': { height: '100% !important' } }}
          />
        </Paper>
      </Box>

      <Paper sx={{ mt: 2, p: 2, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <CompareArrowsIcon color="primary" />
          <Typography>
            <Typography component="span" sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>{stats.additions} Additions</Typography>
            {' / '}
            <Typography component="span" sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>{stats.deletions} Deletions</Typography>
          </Typography>
        </Stack>
        <Paper
          variant="outlined"
          sx={{ p: 2, overflowX: 'auto', fontSize: '1rem', lineHeight: 1.7, maxHeight: '20vh', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
          dangerouslySetInnerHTML={{ __html: diffHtml }}
        />
      </Paper>
    </ToolPageContainer>
    </>
  );
}

export default JsonDiff;
